import { BaseAction, State } from 'state'
import { extractErrorCode } from 'components/utils'
import { CampaignEditorErrorsActions } from './campaignErrors'
import { AuthErrorsActions } from './authErrors'
import { TemplateFormErrorActions } from './campaignTemplates'

export enum ERROR_CODES {
    BRAND_DESCRIPTION_REQUIRED = 'BRAND_DESCRIPTION_REQUIRED',
    BRAND_IMAGE_NOT_FOUND = 'BRAND_IMAGE_NOT_FOUND',
    BRAND_INVALID_COUNTRY = 'BRAND_INVALID_COUNTRY',
    CAMPAIGN_EARLIEST_STEP_HAS_BEEN_PASSED = 'CAMPAIGN_EARLIEST_STEP_HAS_BEEN_PASSED',
    CAMPAIGN_NOT_FOUND = 'CAMPAIGN_NOT_FOUND',
    CAMPAIGN_PAYOUT_DEADLINE_LESS_THAN_END_TIME = 'CAMPAIGN_PAYOUT_DEADLINE_LESS_THAN_END_TIME',
    DUPLICATE_REGISTRATION_RESULT_FOUND = 'DUPLICATE_REGISTRATION_RESULT_FOUND',
    DUPLICATE_SUBMISSION_RESULT_FOUND = 'DUPLICATE_SUBMISSION_RESULT_FOUND',
    END_TIME_MUST_BE_LATER_THAN_START_TIME = 'END_TIME_MUST_BE_LATER_THAN_START_TIME',
    END_TIME_MUST_BE_N_HOURS_LATER_THAN_PREV_STEP = 'END_TIME_MUST_BE_N_HOURS_LATER_THAN_PREV_STEP',
    INPUT_REQUIRED = 'INPUT_REQUIRED',
    NUMBER_TOO_LOW = 'NUMBER_TOO_LOW',
    PAST_SCHEDULE_UPDATE_NOT_ALLOWED = 'PAST_SCHEDULE_UPDATE_NOT_ALLOWED',
    PAYOUT_NOT_ALLOWED_TO_BE_LOWERED = 'PAYOUT_NOT_ALLOWED_TO_BE_LOWERED',
    REGISTRATION_DONT_HAVE_BLOCK = 'REGISTRATION_DONT_HAVE_BLOCK',
    REGISTRATION_MAX_SLOTS_LOWER_THAN_USED = 'REGISTRATION_MAX_SLOTS_LOWER_THAN_USED',
    REGISTRATION_REQUIRES_QUESTION_OR_SUBMISSION_BLOCK = 'REGISTRATION_REQUIRES_QUESTION_OR_SUBMISSION_BLOCK',
    REGISTRATION_RESULT_CANNOT_BE_APPROVED_DUE_TO_BLOCKED_CAMPAIGN = 'REGISTRATION_RESULT_CANNOT_BE_APPROVED_DUE_TO_BLOCKED_CAMPAIGN',
    REGISTRATION_RESULT_HAS_BEEN_REVIEWED = 'REGISTRATION_RESULT_HAS_BEEN_REVIEWED',
    REGISTRATION_SCHEDULE_MUST_END_EARLIER_THAN_TASK = 'REGISTRATION_SCHEDULE_MUST_END_EARLIER_THAN_TASK',
    REGISTRATION_SCHEDULE_MUST_START_EARLIER_THAN_TASK = 'REGISTRATION_SCHEDULE_MUST_START_EARLIER_THAN_TASK',
    REGISTRATION_SLOT_INSUFFICIENT = 'REGISTRATION_SLOT_INSUFFICIENT',
    SCHEDULE_CHANGE_BACKWARD_NOT_ALLOWED = 'SCHEDULE_CHANGE_BACKWARD_NOT_ALLOWED',
    SCHEDULE_MUST_END_LATER_THAN_REGISTRATION = 'SCHEDULE_MUST_END_LATER_THAN_REGISTRATION',
    SCHEDULE_MUST_LATER_THAN_NOW = 'SCHEDULE_MUST_LATER_THAN_NOW',
    SCHEDULE_MUST_LATER_THAN_PARENT_STEP = 'SCHEDULE_MUST_LATER_THAN_PARENT_STEP',
    SCHEDULE_MUST_START_LATER_THAN_REGISTRATION = 'SCHEDULE_MUST_START_LATER_THAN_REGISTRATION',
    SLOT_NUMBER_LESS_THAN_ONE = 'SLOT_NUMBER_LESS_THAN_ONE',
    SUBMISSION_DELETED = 'SUBMISSION_DELETED',
    SUBMISSION_NOT_FOUND = 'SUBMISSION_NOT_FOUND',
    SUBMISSION_PAID = 'SUBMISSION_PAID',
    SUBMISSION_PAYOUT_ON_PROCESS = 'SUBMISSION_PAYOUT_ON_PROCESS',
    SUBMISSION_STATUS_FILLING_FAST_MIN_INVALID = 'SUBMISSION_STATUS_FILLING_FAST_MIN_INVALID',
    SUBMISSION_STATUS_FULL_MIN_LOWER_THAN_FILLING_FAST_MIN = 'SUBMISSION_STATUS_FULL_MIN_LOWER_THAN_FILLING_FAST_MIN',
    TASK_MUST_HAVE_AT_LEAST_ONE_ENGAGEMENT = 'TASK_MUST_HAVE_AT_LEAST_ONE_ENGAGEMENT',
    UPDATE_NOT_ALLOWED = 'UPDATE_NOT_ALLOWED',
    URL_INVALID = 'URL_INVALID',
    USER_NOT_REGISTER_INTEREST = 'USER_NOT_REGISTER_INTEREST',
    USERNAME_FORMAT_INVALID = 'USERNAME_FORMAT_INVALID',
    PERMISSION_DENIED = 'PERMISSION_DENIED',
    CAMPAIGN_BUDGET_INVALID = 'CAMPAIGN_BUDGET_INVALID',
    CAMPAIGN_NO_PAY_PERMISSION_USER = 'CAMPAIGN_NO_PAY_PERMISSION_USER',
}

export type ErrorsState = Record<string, any>

const initialState: ErrorsState = {
    campaignEditor: {
        brand: null,
        budget: null,
        categories: null,
        description: null,
        images: null,
        objective: null,
        registration: null,
        requirements: null,
        submission_status_filling_fast_min: null,
        submission_status_full_min: null,
        payout_deadline_start: null,
        payout_deadline_end: null,
        tasks: null,
        payment: null,
    },
    templateForm: {
        campaign_info: null,
        campaign_schedule: null,
        task_element_data: null,
    },
    auth: {
        email: null,
        new_password1: null,
    },
}

export const errorsReducer = (state: Record<string, any> = initialState, action: BaseAction): Record<string, any> => {
    switch (action.type) {
        case CampaignEditorErrorsActions.SAVE_ERRORS: {
            const errors = Object.keys(action.payload)
            if (!errors || errors.length === 0) return state
            const return_errors: Record<string, any> = extractErrorCode(action.payload)
            return { ...state, campaignEditor: Object.assign({}, state.campaignEditor, return_errors) }
        }
        case CampaignEditorErrorsActions.RESET_ERROR: {
            return { ...state, campaignEditor: initialState.campaignEditor }
        }
        case TemplateFormErrorActions.SAVE_ERRORS: {
            const errors = Object.keys(action.payload)
            if (!errors || errors.length === 0) return state
            const return_errors: Record<string, any> = extractErrorCode(action.payload)
            return { ...state, templateForm: Object.assign({}, state.templateForm, return_errors) }
        }
        case TemplateFormErrorActions.RESET_ERROR: {
            return { ...state, templateForm: initialState.templateForm }
        }
        case AuthErrorsActions.SAVE_ERRORS: {
            const errors: Record<string, string> = {}
            for (const key in action.payload) {
                errors[key] = action.payload[key]['code'] || null
            }
            return { ...state, auth: errors }
        }
        case AuthErrorsActions.RESET_ERROR: {
            return { ...state, auth: initialState.auth }
        }
        default:
            return state
    }
}

export const selectorEditorErrors =
    (fields: string[]) =>
    (state: State): Record<string, any> | null => {
        if (fields && state.errors.campaignEditor) {
            const returnErrors: Record<string, any> = {}
            if (Array.isArray(fields) && fields.length > 0) {
                fields.forEach((field: string) => {
                    if (state.errors.campaignEditor[field]) {
                        returnErrors[field] = state.errors.campaignEditor[field]
                    }
                })
            }
            if (Object.keys(returnErrors).length === 0) {
                return null
            } else {
                return returnErrors
            }
        } else return null
    }

export {
    SET_UP_ERRORS,
    COMPLETE_SET_UP_ERRORS,
    CREATE_TASKS_ERRORS,
    selectSectionErrors,
    selectTaskErrors,
    saveCampaignEditorErrors,
    resetCampaignEditorErrors,
} from './campaignErrors'
